import React, { useMemo, useState } from "react";

import { FileText, ShieldAlert } from "lucide-react";

import AlertDialog from "../common/AlertDialog";
import { Loader } from "../common/loader";
import { postAPIPayload } from "../../apis/postApi";
import { Button } from "../common/customDesignedComponents/button";
import { FormInput } from "../common/customDesignedComponents/FormInput";
import { Label } from "../common/customDesignedComponents/label";
import { TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS, DANIELS_LAW_DISPLAY_NAME } from "../helpers/constants";

const PURPOSE_OPTIONS = [
  { id: "refinance", label: "Refinance" },
  { id: "sale", label: "Sale" },
  { id: "other", label: "Other" },
];

const ACCESS_MODE_OPTIONS = [
  {
    id: "view_only",
    label: "View only on screen",
    description: "The service provider can view the document in their browser. Download is not allowed.",
  },
  {
    id: "download",
    label: "Allow download",
    description: "The service provider may download the source document before access expires.",
  },
];

const defaultExpiryDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toISOString().slice(0, 10);
};

const minExpiryDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().slice(0, 10);
};

const expiryDateToIso = (dateValue) => {
  if (!dateValue) {
    return null;
  }
  return `${dateValue}T23:59:59`;
};

const JesseSourceAccessModal = ({ open, request, onClose, onCompleted }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [usePurpose, setUsePurpose] = useState("refinance");
  const [usePurposeNote, setUsePurposeNote] = useState("");
  const [accessMode, setAccessMode] = useState("view_only");
  const [expiresAt, setExpiresAt] = useState(defaultExpiryDate);

  const lawName = request?.law_display_name || DANIELS_LAW_DISPLAY_NAME;

  const purposeLabel = useMemo(
    () => PURPOSE_OPTIONS.find((option) => option.id === usePurpose)?.label || usePurpose,
    [usePurpose],
  );

  const respond = async (action) => {
    if (!request?.id) {
      return;
    }

    if (action === "approve") {
      if (usePurpose === "other" && !usePurposeNote.trim()) {
        setError("Please describe the intended use when selecting Other.");
        return;
      }
      if (!expiresAt) {
        setError("Please choose when access should expire.");
        return;
      }
    }

    setLoading(true);
    setError("");
    try {
      const body =
        action === "approve"
          ? {
              action,
              use_purpose: usePurpose,
              use_purpose_note: usePurpose === "other" ? usePurposeNote.trim() : null,
              access_mode: accessMode,
              expires_at: expiryDateToIso(expiresAt),
            }
          : { action };

      const result = await postAPIPayload({
        url: `jesse/source-access/${request.id}/respond`,
        data: body,
        token: true,
      });

      if (!result?.success) {
        setError(
          result?.data?.message || result?.message || "Unable to submit your response.",
        );
        return;
      }

      if (action === "approve") {
        const modeLabel = accessMode === "view_only" ? "view-only" : "download";
        setSuccessMessage(
          `Access approved for ${purposeLabel.toLowerCase()} (${modeLabel}) until ${expiresAt}.`,
        );
      } else {
        setSuccessMessage("The source document access request has been denied.");
      }

      onCompleted?.(action);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!open || !request) {
    return null;
  }

  if (successMessage) {
    return (
      <AlertDialog
        type="Success"
        openPopup
        mesage={successMessage}
        onClose={() => {
          setSuccessMessage("");
          onClose?.();
        }}
        footer={false}
      />
    );
  }

  return (
    <div className="tailwind_component fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        className="relative max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-border bg-card p-6 shadow-xl"
      >
        <Loader open={loading} />

        <div className="mb-4 flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary/10">
            <ShieldAlert className="h-5 w-5 text-secondary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Protected document access request
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">
                {request.service_provider_name || "A service provider"}
              </span>{" "}
              is requesting access to a protected {lawName} source document.
            </p>
          </div>
        </div>

        <div className="mb-5 space-y-2 rounded-xl border border-border bg-muted/30 p-4 text-sm">
          <div className="flex items-start gap-2">
            <FileText className="mt-0.5 h-4 w-4 shrink-0 text-secondary" />
            <div>
              <p className="font-medium text-foreground">{request.property_address}</p>
              {request.service_agreement_number ? (
                <p className="mt-1 text-muted-foreground">
                  Agreement # {request.service_agreement_number}
                </p>
              ) : null}
              {request.instrument_type ? (
                <p className="text-muted-foreground">Instrument: {request.instrument_type}</p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold text-foreground">
              Approved use of this document
            </legend>
            <div className="grid gap-2 sm:grid-cols-3">
              {PURPOSE_OPTIONS.map((option) => (
                <label
                  key={option.id}
                  className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                    usePurpose === option.id
                      ? "border-secondary bg-secondary/5"
                      : "border-border"
                  }`}
                >
                  <input
                    type="radio"
                    name="jesse-use-purpose"
                    value={option.id}
                    checked={usePurpose === option.id}
                    onChange={() => {
                      setUsePurpose(option.id);
                      setError("");
                    }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            {usePurpose === "other" ? (
              <div className="space-y-1.5 pt-1">
                <Label htmlFor="jesse-use-purpose-note">Describe the intended use</Label>
                <FormInput
                  id="jesse-use-purpose-note"
                  name="use_purpose_note"
                  value={usePurposeNote}
                  onChange={(event) => setUsePurposeNote(event.target.value)}
                  placeholder="e.g. Estate planning review"
                />
              </div>
            ) : null}
          </fieldset>

          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold text-foreground">Access type</legend>
            {ACCESS_MODE_OPTIONS.map((option) => (
              <label
                key={option.id}
                className={`flex cursor-pointer gap-3 rounded-lg border px-3 py-3 ${
                  accessMode === option.id
                    ? "border-secondary bg-secondary/5"
                    : "border-border"
                }`}
              >
                <input
                  type="radio"
                  name="jesse-access-mode"
                  value={option.id}
                  checked={accessMode === option.id}
                  onChange={() => setAccessMode(option.id)}
                  className="mt-1"
                />
                <span className="text-sm">
                  <span className="block font-medium text-foreground">{option.label}</span>
                  <span className="block text-muted-foreground">{option.description}</span>
                </span>
              </label>
            ))}
          </fieldset>

          <div className="space-y-1.5">
            <Label htmlFor="jesse-access-expires">Access expires on</Label>
            <input
              id="jesse-access-expires"
              type="date"
              min={minExpiryDate()}
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
              className="flex h-11 w-full rounded-lg border border-input bg-background px-3 text-sm"
            />
            <p className="text-xs text-muted-foreground">
              After this date, the service provider will no longer be able to view or download
              the source document.
            </p>
          </div>
        </div>

        {error ? (
          <p className="mt-4 text-sm text-red-600 tailwind_error_msg">{error}</p>
        ) : null}

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className="h-11"
            disabled={loading}
            onClick={() => respond("deny")}
          >
            Deny
          </Button>
          <Button
            type="button"
            className={`${TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS} h-11`}
            disabled={loading}
            onClick={() => respond("approve")}
          >
            {loading ? "Submitting…" : "Approve access"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JesseSourceAccessModal;
